import { Injectable } from '@angular/core';
import { md5 } from 'js-md5';

import { UIRouterGlobals } from '@uirouter/core';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';
import { ObjectHelperService } from 'src/app/shared/services/object-helper.service';
import { environment } from 'src/environments/environment';
import { CompanyStateService } from './company-state.service';
import { RvTokenStoreService } from './rv-token-store.service';

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  accessToken: any;

  _state: any = {
    profile: {}, //Rise vision profile
    user: {}, //Google user
    roleMap: {},
    userToken: this.rvTokenStoreService.read(),
    isRiseAuthUser: false,
    loginMethod: null,
  };

  constructor(
    private uiRouterGlobals: UIRouterGlobals,
    private rvTokenStoreService: RvTokenStoreService,
    private localStorageHelperService: LocalStorageHelperService,
    private objectHelperService: ObjectHelperService,
    private broadcasterService: BroadcasterService,
    private companyStateService: CompanyStateService,
    private userApiService: UserApiService,
  ) {}

  refreshProfile() {
    //populate profile if the current user is a rise vision user
    return this.userApiService
      .getProfile(this._state.user.username, true)
      .then((profile) => {
        this.updateUserProfile(profile);

        //populate company info
        return this.companyStateService.init(profile.companyId);
      });
  }

  isLoggedIn() {
    if (!this._state.user.username) {
      return false;
    } else {
      return true;
    }
  }

  isRiseVisionUser() {
    return (
      this._state.profile.username !== null &&
      this._state.profile.username !== undefined
    );
  }

  hasRole(role) {
    return this._state.roleMap[role] !== undefined;
  }

  setAccessToken(token) {
    this.accessToken = token;
  }

  getAccessToken() {
    return this.accessToken;
  }

  _restoreState() {
    var sFromStorage = this.localStorageHelperService.get(
      'risevision.common.userState',
    );
    if (sFromStorage) {
      Object.assign(this._state, sFromStorage);
      this.localStorageHelperService.remove('risevision.common.userState'); //clear
      console.debug('userState restored with', sFromStorage);
    }
  }

  _resetState() {
    this.objectHelperService.clearObj(this._state.user);
    this.objectHelperService.clearObj(this._state.profile);
    this._state.roleMap = {};

    this.companyStateService.resetCompanyState();
    console.debug('User state has been reset.');
  }

  // user getters
  getUsername() {
    return (this._state.user && this._state.user.username) || null;
  }
  getUserHash() {
    var hash =
      this.getUsername() &&
      this.companyStateService.getUserCompanyId() &&
      md5(
        this.getUsername() + ':' + this.companyStateService.getUserCompanyId(),
      );

    return hash || null;
  }
  getUserFullName() {
    var firstName =
      (this._state.profile && this._state.profile.firstName) || '';
    var lastName = (this._state.profile && this._state.profile.lastName) || '';

    return (firstName + ' ' + lastName).trim();
  }
  getUserEmail() {
    return this._state.profile.email;
  }
  getCopyOfProfile(noFollow?) {
    if (noFollow) {
      return Object.assign({}, this._state.profile);
    } else {
      return this.objectHelperService.follow(this._state.profile);
    }
  }
  getUserPicture() {
    return this._state.user.picture;
  }

  getUserSettings() {
    return this._state.profile.settings;
  }

  private parseNegatedRole(role: string): {
    negated: boolean;
    baseRole: string;
  } {
    const negated = role.charAt(0) === '!';
    const baseRole = negated ? role.substring(1) : role;
    return { negated, baseRole };
  }

  /**
   * Checks if the current user has permission based on roles and environment restrictions.
   *
   * @param roles - Space-separated string of roles and environment restrictions
   *
   * Roles can be of two types:
   * 1. User roles: 'sa', 'ua', 'ba', etc. - define user permissions
   * 2. Environment roles: 'PROD', 'STAGE', 'BETA', 'DEV', 'TEST' - define environment restrictions
   *
   * Roles can be negated with '!' prefix:
   * - 'PROD' = only in production environment
   * - '!PROD' = in any environment except production
   * - '!sa' = not Rise Admin
   *
   * Examples:
   * - 'sa' = user must have 'sa' role
   * - 'PROD' = only in production environment (no user role required)
   * - 'PROD STAGE' = in production and stage environments (no user role required)
   * - 'sa PROD' = user must have 'sa' role AND be in production environment
   * - 'sa !PROD' = user must have 'sa' role AND NOT be in production environment
   *
   * @returns true if permission is granted, false otherwise
   */
  hasPermission(roles: string) {
    if (!roles?.trim()) return true;

    const requiredEnvironmentRoles = roles
      .split(' ')
      .filter((role) => this.isEnvironmentRole(role));

    for (let requiredEnvironment of requiredEnvironmentRoles) {
      const { negated, baseRole } = this.parseNegatedRole(requiredEnvironment);

      if (!negated && !this.checkEnvironment(baseRole)) {
        return false;
      } else if (negated && this.checkEnvironment(baseRole)) {
        return false;
      }
    }

    const requiredUserRoles = roles
      .split(' ')
      .filter((role) => !this.isEnvironmentRole(role));
    if (!requiredUserRoles.length) return true;

    for (let requiredUserRole of requiredUserRoles) {
      const { negated, baseRole } = this.parseNegatedRole(requiredUserRole);

      if (this.hasRole(baseRole)) {
        if (!negated) {
          return true;
        }
      } else if (negated) {
        return true;
      }
    }

    return false;
  }

  isRiseAdmin() {
    return this.hasRole('sa') && this.companyStateService.isRootCompany();
  }
  isBillingAdmin() {
    return this.hasRole('ba');
  }
  canAccessBilling() {
    return this.hasPermission('sa ua ba');
  }
  isUserAdmin() {
    return this.hasRole('ua');
  }
  isRiseAuthUser() {
    return this._state.isRiseAuthUser;
  }
  isSignUp() {
    return !!this._state.isSignUp;
  }
  isAssignedPublisher() {
    return this.hasRole('ap');
  }
  isModerator() {
    return this.hasRole('sm');
  }

  isEnvironmentRole(role: string) {
    return ['PROD', 'STAGE', 'BETA', 'TEST', 'DEV'].includes(
      role.replaceAll('!', ''),
    );
  }

  checkEnvironment(environmentRole: string) {
    if (environmentRole === 'PROD') {
      return environment.ENV_NAME === null;
    }
    return environment.ENV_NAME === environmentRole;
  }

  // user functions
  checkUsername(username?) {
    return (
      (username || false) &&
      (this.getUsername() || false) &&
      username.toUpperCase() === this.getUsername().toUpperCase()
    );
  }
  updateUserProfile(user) {
    if (this.checkUsername(user.username)) {
      this.objectHelperService.clearAndCopy(
        Object.assign(
          {
            username: this._state.user.username,
          },
          user,
        ),
        this._state.profile,
      );

      //set role map
      this._state.roleMap = {};
      if (this._state.profile.roles) {
        this._state.profile.roles.forEach((val) => {
          this._state.roleMap[val] = true;
        });
      }

      this.broadcasterService.emit('risevision.user.updated');
    }
  }
  getLoginMethod() {
    return this._state.loginMethod;
  }
  setLoginMethod(method) {
    this._state.loginMethod = method;
  }

  _persistState(
    isSignUp?: boolean,
    replacePath?: string,
    replaceSearchParameters?: URLSearchParams,
  ) {
    let redirectState: { p?: string; s?: string; u?: string } = {};
    if (this.uiRouterGlobals.params.state) {
      redirectState = JSON.parse(
        decodeURIComponent(this.uiRouterGlobals.params.state),
      );
    }

    if (replacePath) redirectState.p = replacePath;
    if (replaceSearchParameters)
      redirectState.s = '?' + replaceSearchParameters.toString();

    const redirectStateStr = encodeURIComponent(JSON.stringify(redirectState));

    this._state.redirectState = redirectStateStr;
    this._state.isSignUp = !!isSignUp;
    // persist user state
    this.localStorageHelperService.set(
      'risevision.common.userState',
      this._state,
    );

    return redirectStateStr;
  }

  _setIsRiseAuthUser(isRiseAuthUser) {
    this._state.isRiseAuthUser = isRiseAuthUser;
  }
}
